import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import WComponent from "../../../../common/WComponent";
import { AJE_TEMPLATE_GROUPS } from "./helpers/constants";
import "../afs-walkthrough.css";

const BALANCE_TOLERANCE = 0.01;

let _tempIdCounter = 1;
function nextTempId() {
	_tempIdCounter += 1;
	return _tempIdCounter;
}

// Each line in the form holds both the AJE data (account/side/amount/canonical)
// AND the per-line picker ephemera (search text, dropdown open). Keeping them on
// the same object means line ops (add/remove/reorder) move the picker state with
// the row instead of stranding it on a stale index.
function mkLine(side) {
	return {
		tempId: nextTempId(),
		account: "",
		accountDescription: "",
		canonicalCode: null,
		side: side || "DEBIT",
		amount: "",
		search: "",
		showPicker: false,
	};
}

function blankFormLines() {
	return [mkLine("DEBIT"), mkLine("CREDIT")];
}

class AJEModal extends WComponent {
	componentDidUpdate(prevProps) {
		// Consume a fresh `draft` (passed from AFSWalkthrough when the user clicks
		// "Edit" on a cascade proposal). New non-null draft → prefill formLines +
		// description and tell the parent we've consumed it so it doesn't re-apply.
		if (this.props.draft && this.props.draft !== prevProps.draft) {
			const draftLines = (this.props.draft.lines || []).map((l) => ({
				tempId: nextTempId(),
				account: l.account || "",
				accountDescription: l.accountDescription || "",
				canonicalCode: l.canonicalCode || null,
				side: l.side === "CREDIT" ? "CREDIT" : "DEBIT",
				amount: String(Math.abs(Number(l.amount) || 0) || ""),
				search: "",
				showPicker: false,
			}));
			if (!draftLines.some(l => l.side === "DEBIT")) draftLines.push(mkLine("DEBIT"));
			if (!draftLines.some(l => l.side === "CREDIT")) draftLines.push(mkLine("CREDIT"));
			this.setState({
				formLines: draftLines,
				description: this.props.draft.description || "",
				activeTemplate: null,
				editingIndex: null,
				creatingForLineId: null,
			});
			if (this.props.onDraftConsumed) this.props.onDraftConsumed();
		}
	}

	state = {
		formLines: blankFormLines(),
		description: "",
		activeTemplate: null,
		// Index of the entry currently being edited; null when adding a new entry.
		editingIndex: null,
		// Inline "create new account" form — opened from inside a line's picker
		// dropdown when the account doesn't exist yet. Holds the line's tempId so
		// we know which row to populate on success.
		creatingForLineId: null,
		newAccountNumber: "",
		newAccountName: "",
		newCanonicalCode: null,        // required — anchors the account in the FS rollup
		newCanonicalLabel: "",
		newCanonicalSearch: "",
		newAccountSaving: false,
		newAccountError: null,
	};

	render() {
	let { open, onClose, entries, onChange, chartOfAccounts, canonicalTaxonomyOptions, onCreateChartOfAccount } = this.props;
	let { formLines, description, activeTemplate, editingIndex, creatingForLineId, newAccountNumber, newAccountName, newCanonicalCode, newCanonicalLabel, newCanonicalSearch, newAccountSaving, newAccountError } = this.state;

	const accountOptions = (chartOfAccounts || []).map(a => {
		const number = a.accountNumber || a.AC_NO || "";
		const name = a.accountName || a.accountDescription || a.display_name || a.description || "";
		return {
			value: number,
			label: number ? `${number} - ${name}` : name,
			gifi_code: a.gifi_code,
		};
	}).filter(a => a.value);

	if (!open) return null;

	const filterOptions = (query) => {
		const q = (query || "").trim().toLowerCase();
		if (!q) return accountOptions.slice(0, 8);
		return accountOptions.filter(opt =>
			opt.label.toLowerCase().includes(q) || String(opt.gifi_code || "").includes(q)
		).slice(0, 8);
	};

	const findAccountByGifi = (gifi) => {
		if (!gifi) return null;
		const matches = accountOptions.filter(a => Number(a.gifi_code) === Number(gifi));
		if (matches.length !== 1) return null;
		return matches[0].value;
	};

	const findLabel = (acNo) => {
		const a = accountOptions.find(x => x.value === acNo);
		return a ? a.label : acNo;
	};

	const fmtAmt = v => Number(v).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

	// ──────────────────────────────────────────────────────────────────────
	// Form line operations
	// ──────────────────────────────────────────────────────────────────────

	const patchLine = (tempId, patch) => {
		const next = formLines.map(l => l.tempId === tempId ? { ...l, ...patch } : l);
		this.update({ formLines: next });
	};

	const addLine = (side) => {
		this.update({ formLines: [...formLines, mkLine(side)] });
	};

	const removeLine = (tempId) => {
		const next = formLines.filter(l => l.tempId !== tempId);
		// Always keep at least one DR row and one CR row in the form skeleton.
		const hasDR = next.some(l => l.side === "DEBIT");
		const hasCR = next.some(l => l.side === "CREDIT");
		const padded = next.slice();
		if (!hasDR) padded.push(mkLine("DEBIT"));
		if (!hasCR) padded.push(mkLine("CREDIT"));
		this.update({ formLines: padded });
	};

	// Per-line amount lives in two virtual columns (Debit / Credit). Typing in
	// the DR column sets side=DEBIT; typing in the CR column sets side=CREDIT.
	// Switching columns clears the other.
	const setLineAmount = (tempId, side, value) => {
		patchLine(tempId, { side, amount: value });
	};

	// ──────────────────────────────────────────────────────────────────────
	// Balance + save
	// ──────────────────────────────────────────────────────────────────────

	const lineAmount = (l) => Math.abs(Number(l.amount) || 0);
	const totalDR = formLines.filter(l => l.side === "DEBIT").reduce((s, l) => s + lineAmount(l), 0);
	const totalCR = formLines.filter(l => l.side === "CREDIT").reduce((s, l) => s + lineAmount(l), 0);
	const diff = totalDR - totalCR;
	const isBalanced = Math.abs(diff) <= BALANCE_TOLERANCE && totalDR > 0;

	// A line is "filled" if it has an account AND a positive amount. We save
	// only filled lines, so a half-filled row at the bottom (typical when the
	// user is mid-input) doesn't break anything.
	const filledLines = formLines.filter(l => l.account && lineAmount(l) > 0);
	const canSave = isBalanced
		&& filledLines.length >= 2
		&& filledLines.some(l => l.side === "DEBIT")
		&& filledLines.some(l => l.side === "CREDIT");

	const isEditing = editingIndex !== null;

	const resetForm = () => {
		this.update({ formLines: blankFormLines() });
		this.update({ description: "" });
		this.update({ activeTemplate: null });
		this.update({ editingIndex: null });
		this.update({ creatingForLineId: null });
	};

	const loadEntryForEdit = (index) => {
		const e = entries[index];
		if (!e) return;
		const lines = (e.lines || []).map(l => ({
			tempId: nextTempId(),
			account: l.account || "",
			accountDescription: l.accountDescription || "",
			canonicalCode: l.canonicalCode || null,
			side: l.side === "CREDIT" ? "CREDIT" : "DEBIT",
			amount: String(Math.abs(Number(l.amount) || 0) || ""),
			search: "",
			showPicker: false,
		}));
		// Pad to at least 1 DR + 1 CR so the form skeleton stays usable.
		if (!lines.some(l => l.side === "DEBIT")) lines.push(mkLine("DEBIT"));
		if (!lines.some(l => l.side === "CREDIT")) lines.push(mkLine("CREDIT"));
		this.update({ formLines: lines });
		this.update({ description: e.description || "" });
		this.update({ activeTemplate: null });
		this.update({ editingIndex: index });
		this.update({ creatingForLineId: null });
		setTimeout(() => {
			const el = document.querySelector(".afs-aje__form");
			if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
		}, 0);
	};

	const addEntry = () => {
		if (!canSave) return;
		const lines = filledLines.map(l => {
			const opt = accountOptions.find(o => o.value === l.account);
			return {
				account: l.account,
				accountDescription: l.accountDescription || (opt ? opt.label : null),
				canonicalCode: l.canonicalCode || null,
				side: l.side,
				amount: lineAmount(l),
			};
		});
		const summary = lines.length === 2
			? `AJE: DR ${lines[0].side === "DEBIT" ? lines[0].account : lines[1].account} / CR ${lines[0].side === "CREDIT" ? lines[0].account : lines[1].account}`
			: `AJE: ${lines.length}-line journal entry`;
		const updatedEntry = {
			lines,
			description: description || summary,
			source: "human",
			status: "accepted",
			date: new Date().toISOString().split("T")[0],
		};
		if (isEditing) {
			const original = entries[editingIndex] || {};
			const originalAmount = entryAmount(original);
			const newAmount = entryAmount(updatedEntry);
			const merged = {
				...original,
				...updatedEntry,
				edited_at: new Date().toISOString(),
				edited_from_amount: originalAmount !== newAmount ? originalAmount : (original.edited_from_amount || null),
			};
			const next = entries.slice();
			next[editingIndex] = merged;
			onChange(next);
		} else {
			onChange([...entries, updatedEntry]);
		}
		resetForm();
	};

	const removeEntry = (index) => {
		onChange(entries.filter((_, i) => i !== index));
		if (editingIndex === index) resetForm();
	};

	// ──────────────────────────────────────────────────────────────────────
	// Templates
	// ──────────────────────────────────────────────────────────────────────

	const applyTemplate = (tmpl) => {
		const dr = findAccountByGifi(tmpl.debitGifi) || "";
		const cr = findAccountByGifi(tmpl.creditGifi) || "";
		const drOpt = accountOptions.find(o => o.value === dr);
		const crOpt = accountOptions.find(o => o.value === cr);
		this.update({
			activeTemplate: tmpl.label,
			description: tmpl.description || tmpl.label || "",
			formLines: [
				{ tempId: nextTempId(), account: dr, accountDescription: drOpt ? drOpt.label : "", canonicalCode: null, side: "DEBIT", amount: "", search: "", showPicker: false },
				{ tempId: nextTempId(), account: cr, accountDescription: crOpt ? crOpt.label : "", canonicalCode: null, side: "CREDIT", amount: "", search: "", showPicker: false },
			],
		});
	};

	const activeTemplateObj = AJE_TEMPLATE_GROUPS
		.flatMap(g => g.templates)
		.find(t => t.label === activeTemplate);

	const firstDR = formLines.find(l => l.side === "DEBIT");
	const firstCR = formLines.find(l => l.side === "CREDIT");
	const debitHint = activeTemplateObj && firstDR && !firstDR.account ? activeTemplateObj.debitHint : null;
	const creditHint = activeTemplateObj && firstCR && !firstCR.account ? activeTemplateObj.creditHint : null;

	// Per-entry amount = sum of DR side (= sum of CR side when balanced).
	const entryAmount = (e) => {
		let sum = 0;
		for (const l of (e.lines || [])) if (l.side === "DEBIT") sum += Math.abs(Number(l.amount) || 0);
		return sum;
	};

	const totalEntries = entries.reduce((sum, e) => sum + entryAmount(e), 0);

	// ──────────────────────────────────────────────────────────────────────
	// New-account inline form (unchanged behavior, just keyed by lineId)
	// ──────────────────────────────────────────────────────────────────────

	const openCreateForm = (lineId, prefillName) => {
		this.update({
			creatingForLineId: lineId,
			newAccountNumber: "",
			newAccountName: prefillName || "",
			newCanonicalCode: null,
			newCanonicalLabel: "",
			newCanonicalSearch: "",
			newAccountError: null,
			newAccountSaving: false,
		});
	};

	const closeCreateForm = () => {
		this.update({ creatingForLineId: null, newAccountError: null, newAccountSaving: false });
	};

	const filterCanonical = (query) => {
		const list = canonicalTaxonomyOptions || [];
		const q = (query || "").trim().toLowerCase();
		if (!q) return list.slice(0, 8);
		return list.filter(t =>
			(t.display_name || "").toLowerCase().includes(q) ||
			(t.canonical_code || "").toLowerCase().includes(q) ||
			String(t.gifi_code || "").includes(q)
		).slice(0, 8);
	};

	const submitCreateForm = async (lineId) => {
		if (!onCreateChartOfAccount) {
			this.update({ newAccountError: "Account creation isn't available in this context" });
			return;
		}
		if (!newAccountNumber.trim() || !newAccountName.trim()) {
			this.update({ newAccountError: "Account number and name are required" });
			return;
		}
		if (!newCanonicalCode) {
			this.update({ newAccountError: "Pick a canonical account or GIFI line — this anchors where the new account rolls up on the FS" });
			return;
		}
		this.update({ newAccountSaving: true, newAccountError: null });
		const result = await onCreateChartOfAccount({
			accountNumber: newAccountNumber.trim(),
			accountName: newAccountName.trim(),
			canonicalCode: newCanonicalCode,
		});
		if (result && result.account) {
			patchLine(lineId, {
				account: result.account.accountNumber,
				accountDescription: `${result.account.accountNumber} - ${result.account.accountName}`,
				canonicalCode: result.account.canonical_code,
				search: "",
				showPicker: false,
			});
			this.update({ creatingForLineId: null, newAccountSaving: false, newAccountNumber: "", newAccountName: "", newCanonicalCode: null, newCanonicalLabel: "", newCanonicalSearch: "" });
		} else {
			this.update({ newAccountSaving: false, newAccountError: (result && result.error) || "Failed to create account" });
		}
	};

	const renderCreateForm = (lineId) => (
		<div className="afs-aje-pad__account-dropdown afs-aje__create-account">
			<div className="afs-aje__create-account-title">Add new account</div>
			<input
				className="afs-aje-pad__account-input"
				type="text"
				placeholder="Account number (e.g. 1245)"
				value={newAccountNumber}
				onChange={e => this.update({ newAccountNumber: e.target.value })}
				autoFocus
			/>
			<input
				className="afs-aje-pad__account-input"
				type="text"
				placeholder="Account name"
				value={newAccountName}
				onChange={e => this.update({ newAccountName: e.target.value })}
			/>
			<div className="afs-aje__create-account-canonical">
				<label className="afs-aje__create-account-label">Map to canonical / GIFI line</label>
				{newCanonicalCode ? (
					<div className="afs-aje-pad__account-selected">
						<span>{newCanonicalLabel}</span>
						<button className="afs-aje-pad__account-clear" onClick={() => this.update({ newCanonicalCode: null, newCanonicalLabel: "", newCanonicalSearch: "" })} title="Clear"><Icon.X size={12} /></button>
					</div>
				) : (
					<>
						<input
							className="afs-aje-pad__account-input"
							type="text"
							placeholder="Search canonical name or GIFI code…"
							value={newCanonicalSearch}
							onChange={e => this.update({ newCanonicalSearch: e.target.value })}
						/>
						<div className="afs-aje__canonical-dropdown">
							{(canonicalTaxonomyOptions || []).length === 0 && (
								<div className="afs-aje__picker-empty">Canonical taxonomy not loaded</div>
							)}
							{filterCanonical(newCanonicalSearch).map(t => (
								<button
									key={t.canonical_code}
									className="afs-aje-pad__account-option"
									onClick={() => this.update({ newCanonicalCode: t.canonical_code, newCanonicalLabel: t.display_name + (t.gifi_code ? ` (GIFI ${t.gifi_code})` : "") })}
								>
									<span>{t.display_name}</span>
									{t.gifi_code ? <span className="afs-aje-pad__account-gifi">GIFI {t.gifi_code}</span> : null}
								</button>
							))}
						</div>
					</>
				)}
			</div>
			{newAccountError && <div className="afs-aje__create-account-error">{newAccountError}</div>}
			<div className="afs-aje__create-account-actions">
				<button className="afs-aje__btn" onClick={closeCreateForm} disabled={newAccountSaving}>Cancel</button>
				<button className="afs-aje__btn afs-aje__btn--primary" onClick={() => submitCreateForm(lineId)} disabled={newAccountSaving}>
					{newAccountSaving ? "Adding…" : "Add account"}
				</button>
			</div>
		</div>
	);

	// ──────────────────────────────────────────────────────────────────────
	// Per-line account picker (same shape as before, just per-row)
	// ──────────────────────────────────────────────────────────────────────

	const renderPicker = (line) => (
		<div className="afs-aje-pad__account-picker">
			{line.account ? (
				<div className="afs-aje-pad__account-selected">
					<span>{findLabel(line.account)}</span>
					<button
						className="afs-aje-pad__account-clear"
						onClick={() => patchLine(line.tempId, { account: "", accountDescription: "", canonicalCode: null, search: "", showPicker: true })}
						title="Clear"
					>
						<Icon.X size={12} />
					</button>
				</div>
			) : creatingForLineId === line.tempId ? (
				renderCreateForm(line.tempId)
			) : (
				<>
					<input
						className="afs-aje-pad__account-input"
						type="text"
						placeholder="Search account…"
						value={line.search}
						onChange={e => patchLine(line.tempId, { search: e.target.value, showPicker: true })}
						onFocus={() => {
							// Close every other picker — only one open at a time.
							this.update({
								formLines: formLines.map(l => l.tempId === line.tempId ? { ...l, showPicker: true } : { ...l, showPicker: false }),
							});
						}}
					/>
					{line.showPicker && (
						<div className="afs-aje-pad__account-dropdown">
							{filterOptions(line.search).map(opt => (
								<button
									key={opt.value}
									className="afs-aje-pad__account-option"
									onClick={() => patchLine(line.tempId, { account: opt.value, accountDescription: opt.label, showPicker: false, search: "" })}
								>
									<span>{opt.label}</span>
									{opt.gifi_code ? <span className="afs-aje-pad__account-gifi">GIFI {opt.gifi_code}</span> : null}
								</button>
							))}
							{filterOptions(line.search).length === 0 && (
								<div className="afs-aje__picker-empty">No matching accounts</div>
							)}
							{onCreateChartOfAccount && (
								<button
									className="afs-aje-pad__account-option afs-aje__add-account"
									onClick={() => openCreateForm(line.tempId, line.search)}
								>
									<Icon.Plus size={12} className="afs-aje__inline-icon" />
									<span>Add new account{line.search ? ` "${line.search}"` : ""}</span>
								</button>
							)}
						</div>
					)}
				</>
			)}
		</div>
	);

	return (
		<div className="afs-aje__overlay" onClick={onClose}>
			<div className="afs-aje__modal" onClick={e => e.stopPropagation()}>
				<div className="afs-aje__header">
					<h2 className="afs-aje__title">Adjusting Journal Entries</h2>
					<p className="afs-aje__subtitle">Year-end adjustments for non-cash items (depreciation, accruals, tax provision, etc.)</p>
				</div>
				<div className="afs-aje__body">

					{/* Templates */}
					<div className="afs-aje__templates">
						<span className="afs-aje__label">Quick Templates</span>
						{AJE_TEMPLATE_GROUPS.map(group => (
							<div key={group.label} className="afs-aje__template-group">
								<span className="afs-aje__template-group-label">{group.label}</span>
								<div className="afs-aje__template-row">
									{group.templates.map(t => (
										<span
											key={t.label}
											className={clsx("afs-aje__template", activeTemplate === t.label && "afs-aje__template--active")}
											onClick={() => applyTemplate(t)}
											title={t.description}
										>
											{t.label}
										</span>
									))}
								</div>
							</div>
						))}
					</div>

					{/* New Entry Form — multi-line journal */}
					<div className="afs-aje__form">
						<div className="afs-aje__lines-header">
							<span className="afs-aje__lines-col afs-aje__lines-col--account">Account</span>
							<span className="afs-aje__lines-col afs-aje__lines-col--amount">Debit</span>
							<span className="afs-aje__lines-col afs-aje__lines-col--amount">Credit</span>
							<span className="afs-aje__lines-col afs-aje__lines-col--remove" />
						</div>
						{formLines.map((line, idx) => {
							const showDebitHint = idx === 0 && debitHint && !line.account && line.side === "DEBIT";
							const showCreditHint = idx === 1 && creditHint && !line.account && line.side === "CREDIT";
							return (
								<div key={line.tempId} className="afs-aje__line">
									<div className="afs-aje__lines-col afs-aje__lines-col--account">
										{renderPicker(line)}
										{showDebitHint && <div className="afs-aje__hint"><Icon.Info size={11} /> {debitHint}</div>}
										{showCreditHint && <div className="afs-aje__hint"><Icon.Info size={11} /> {creditHint}</div>}
									</div>
									<div className="afs-aje__lines-col afs-aje__lines-col--amount">
										<input
											className="afs-aje__input afs-aje__input--amount"
											type="number"
											min="0"
											step="0.01"
											placeholder="0.00"
											value={line.side === "DEBIT" ? line.amount : ""}
											onChange={e => setLineAmount(line.tempId, "DEBIT", e.target.value)}
										/>
									</div>
									<div className="afs-aje__lines-col afs-aje__lines-col--amount">
										<input
											className="afs-aje__input afs-aje__input--amount"
											type="number"
											min="0"
											step="0.01"
											placeholder="0.00"
											value={line.side === "CREDIT" ? line.amount : ""}
											onChange={e => setLineAmount(line.tempId, "CREDIT", e.target.value)}
										/>
									</div>
									<div className="afs-aje__lines-col afs-aje__lines-col--remove">
										<button
											className="afs-aje__line-remove"
											onClick={() => removeLine(line.tempId)}
											title="Remove line"
											disabled={formLines.length <= 2}
										>
											<Icon.X size={14} />
										</button>
									</div>
								</div>
							);
						})}
						<div className="afs-aje__add-line-row">
							<button className="afs-aje__add-line" onClick={() => addLine("DEBIT")}>
								<Icon.Plus size={12} className="afs-aje__inline-icon" /> Add line
							</button>
							<div className="afs-aje__balance">
								<span className="afs-aje__balance-cell">DR ${fmtAmt(totalDR)}</span>
								<span className="afs-aje__balance-cell">CR ${fmtAmt(totalCR)}</span>
								<span className={clsx("afs-aje__balance-status", isBalanced ? "afs-aje__balance-status--ok" : "afs-aje__balance-status--off")}>
									{isBalanced ? "Balanced" : `Out of balance by $${fmtAmt(Math.abs(diff))}`}
								</span>
							</div>
						</div>
						<div className="afs-aje__row">
							<div className="afs-aje__col afs-aje__col--wide">
								<label className="afs-aje__label">Description</label>
								<input className="afs-aje__input" placeholder="e.g. Year-end depreciation" value={description} onChange={e => this.update({ description: e.target.value })} />
							</div>
						</div>
						<div className="afs-aje__form-actions">
							{isEditing && (
								<button className="afs-aje__btn afs-aje__btn--spaced" onClick={resetForm}>Cancel</button>
							)}
							<button className={clsx("afs-aje__btn", "afs-aje__btn--primary", !canSave && "afs-aje__btn--disabled")} disabled={!canSave} onClick={addEntry}>
								{isEditing ? `Update Entry #${editingIndex + 1}` : "Add Entry"}
							</button>
						</div>
					</div>

					{/* Existing Entries */}
					{entries.length > 0 && (
						<>
							<div className="afs-aje__entries-header">
								<span className="afs-aje__label">Entries ({entries.length})</span>
								<span className="afs-aje__total">Total: ${fmtAmt(totalEntries)}</span>
							</div>
							{entries.map((e, i) => {
								const isAgent = e.source === "ai_agent" || e.source === "agent" || (e.agent_run_id != null);
								const isThisEditing = editingIndex === i;
								const wasEdited = !!e.edited_at;
								const entryLines = e.lines || [];
								return (
									<div
										key={i}
										className={clsx("afs-aje__entry", isThisEditing && "afs-aje__entry--editing", isAgent && !isThisEditing && "afs-aje__entry--agent")}
										onClick={() => loadEntryForEdit(i)}
										title="Click to edit"
									>
										<div className="afs-aje__entry-desc">
											{entryLines.map((l, li) => (
												<div key={li}>
													<strong>{l.side === "DEBIT" ? "DR" : "CR"}</strong> {findLabel(l.account)}
													{entryLines.length > 2 ? <span className="afs-aje__entry-line-amt"> ${fmtAmt(l.amount)}</span> : null}
												</div>
											))}
											{e.description && <div className="afs-aje__entry-note">{e.description}</div>}
											{isAgent && (
												<div className="afs-aje__agent-tag">
													<Icon.Cpu size={11} className="afs-aje__inline-icon" />
													Booked by Agent Mode
													{e.check_id ? ` (${e.check_id})` : ""}
													{wasEdited ? ` · edited from $${fmtAmt(e.edited_from_amount)}` : ""}
												</div>
											)}
											{isAgent && e.agent_rationale && (
												<details className="afs-aje__agent-reasoning" onClick={ev => ev.stopPropagation()}>
													<summary className="afs-aje__agent-reasoning-summary">
														Show agent reasoning ({(e.agent_evidence || []).length} step{(e.agent_evidence || []).length === 1 ? "" : "s"})
													</summary>
													<div className="afs-aje__agent-evidence">
														{(e.agent_evidence || []).map((step, j) => (
															<div key={j} className="afs-aje__agent-evidence-row">
																<strong className="afs-aje__agent-evidence-step">{step.step}:</strong> <span className="afs-aje__agent-evidence-result">{step.result}</span>
															</div>
														))}
														{(!e.agent_evidence || e.agent_evidence.length === 0) && (
															<div className="afs-aje__agent-rationale-fallback">{e.agent_rationale}</div>
														)}
													</div>
												</details>
											)}
										</div>
										<div className="afs-aje__entry-amt">${fmtAmt(entryAmount(e))}</div>
										<button className="afs-aje__entry-delete" onClick={ev => { ev.stopPropagation(); removeEntry(i); }} title="Remove">×</button>
									</div>
								);
							})}
						</>
					)}

					{entries.length === 0 && (
						<p className="afs-aje__empty">
							No adjusting entries yet. Add entries above for depreciation, accruals, tax provisions, etc.
						</p>
					)}
				</div>
				<div className="afs-aje__footer">
					<button className="afs-aje__btn" onClick={onClose}>Close</button>
				</div>
			</div>
		</div>
	);
	}
}

export default AJEModal;
